import { BarChart, LineChart } from "echarts/charts";
import { GridComponent, LegendComponent } from "echarts/components";
import * as echarts from "echarts/core";
import type { EChartsCoreOption } from "echarts/core";
import { SVGRenderer } from "echarts/renderers";

import type { Dataset, MeasurementPanel, PanelData } from "./api";
import { normalizePanelChartData } from "./dashboardChartData";
import { measurementAxisFormatter, measurementAxisValues, usesDailyMeasurementAxis } from "./measurementChartAxis";
import { panelDataForTrafficView, trafficViewKnownClassValues, type TrafficView } from "./trafficViews";
import { runtimeDimensionValueLabel } from "./panelFilters";
import { isVehicleClass, vehicleClassColor, vehicleClassLabel } from "./vehicleClasses";

echarts.use([BarChart, GridComponent, LegendComponent, LineChart, SVGRenderer]);

export type PanelChartExportSize = { width: number; height: number };
export type PanelChartExporter = (size: PanelChartExportSize) => SVGSVGElement | null;

const exporters = new Map<string, PanelChartExporter>();

export function registerPanelChartExporter(panelID: string, exporter: PanelChartExporter) {
  exporters.set(panelID, exporter);
  return () => {
    if (exporters.get(panelID) === exporter) exporters.delete(panelID);
  };
}

export function exportPanelChartSVG(panelID: string, size: PanelChartExportSize) {
  return exporters.get(panelID)?.(size) ?? null;
}

const reportSeriesColors = ["#2e6684", "#6c8796", "#846b55", "#64755a", "#7b6687"];

function reportGroupLabel(groups: Record<string, unknown>, vehicleClasses: boolean, dataset?: Dataset) {
  const values = Object.entries(groups).map(([name, value]) => vehicleClasses && name === "class" ? vehicleClassLabel(value) : runtimeDimensionValueLabel(dataset, name, value));
  return values.length ? values.join(" · ") : "Gesamt";
}

function reportSeriesColor(groups: Record<string, unknown>, index: number, vehicleClasses: boolean) {
  return vehicleClasses && Object.hasOwn(groups, "class") ? vehicleClassColor(groups.class) : reportSeriesColors[index % reportSeriesColors.length];
}

/**
 * Creates an SVG only from the report element's immutable configuration and
 * structured data. It deliberately does not consult a visible dashboard chart.
 */
export function renderPanelChartSVG(panel: MeasurementPanel, data: PanelData, dataset: Dataset | undefined, size: PanelChartExportSize, trafficView: TrafficView = "all") {
  const visibleData = panelDataForTrafficView(data, trafficView);
  const rawClassValues = dataset?.fields.find((field) => field.name === "class")?.values?.filter(isVehicleClass) ?? [];
  const classValues = trafficViewKnownClassValues(rawClassValues, trafficView);
  const chart = normalizePanelChartData(visibleData, panel, classValues);
  if (!chart) return null;
  const vehicleClasses = panel.group_by.includes("class") && classValues.length > 0;
  const pixelsPerMillimetre = Math.max(4, 280 / size.width, 150 / size.height);
  const width = Math.max(1, Math.round(size.width * pixelsPerMillimetre));
  const height = Math.max(1, Math.round(size.height * pixelsPerMillimetre));
  const dailyAxis = usesDailyMeasurementAxis(chart.bucketSeconds);
  const dailyAxisValues = measurementAxisValues(chart);
  const formatDate = measurementAxisFormatter(visibleData.timezone ?? "Europe/Vienna", chart.bucketSeconds, chart.rangeEnd - chart.rangeStart);
  const option: EChartsCoreOption = {
    animation: false,
    color: chart.series.map((series, index) => reportSeriesColor(series.groups, index, vehicleClasses)),
    grid: { top: chart.series.length > 1 ? 34 : 15, right: 10, bottom: 28, left: 48 },
    legend: chart.series.length > 1 ? { top: 0, left: 42, right: 8, itemWidth: 12, itemHeight: 3, textStyle: { color: "#707070", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 9 } } : { show: false },
    xAxis: {
      type: dailyAxis ? "category" : "time",
      ...(dailyAxis ? { data: dailyAxisValues, boundaryGap: panel.type === "histogram" } : { min: chart.rangeStart, max: chart.rangeEnd, boundaryGap: false }),
      axisLine: { lineStyle: { color: "#cfd2d6" } },
      axisTick: { show: false },
      axisLabel: { color: "#949a9e", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 9, hideOverlap: true, formatter: (value: string | number) => formatDate(Number(value)) },
      splitLine: { show: true, lineStyle: { color: "#e4e7e9", type: "dashed" } },
    },
    yAxis: {
      type: "value",
      name: panel.format === "percent" ? "%" : dataset?.fields.find((field) => field.name === panel.measure)?.unit || "Anzahl",
      nameTextStyle: { color: "#707070", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 8 },
      axisLine: { show: true, lineStyle: { color: "#cfd2d6" } },
      axisTick: { show: false },
      axisLabel: { color: "#949a9e", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 9 },
      splitLine: { show: true, lineStyle: { color: "#e4e7e9" } },
    },
    series: chart.series.map((series, index) => {
      const color = reportSeriesColor(series.groups, index, vehicleClasses);
      const common = { name: reportGroupLabel(series.groups, vehicleClasses, dataset), data: dailyAxis ? series.points.map((point) => point.value) : series.points.map((point) => [point.timestamp, point.value]), itemStyle: { color } };
      return panel.type === "histogram"
        ? { ...common, type: "bar", stack: "report-total", barMaxWidth: 38 }
        : { ...common, type: "line", connectNulls: false, symbol: "none", showSymbol: false, lineStyle: { color, width: 2 } };
    }),
  };
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  Object.assign(host.style, { position: "fixed", left: "-10000px", top: "0", width: `${width}px`, height: `${height}px`, pointerEvents: "none" });
  document.body.append(host);
  const instance = echarts.init(host, undefined, { renderer: "svg", width, height });
  try {
    instance.setOption(option, { notMerge: true, lazyUpdate: false });
    const svg = host.querySelector("svg")?.cloneNode(true) as SVGSVGElement | undefined;
    svg?.setAttribute("data-x-axis-type", dailyAxis ? "category" : "time");
    svg?.setAttribute("data-x-axis-days", dailyAxisValues.map((timestamp) => formatDate(timestamp)).join("|"));
    svg?.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg?.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg?.querySelectorAll("text").forEach((element) => {
      element.setAttribute("font-family", "Helvetica, Arial, sans-serif");
      element.style.fontFamily = "Helvetica, Arial, sans-serif";
    });
    return svg ?? null;
  } finally {
    instance.dispose();
    host.remove();
  }
}
